// Preact lifecycle wiring for the game board, extracted from
// GameBoard.jsx. componentDidMount builds the Three.js scene, physics
// world, listeners, controllers, restores any saved session, and kicks
// the render loop; componentWillUnmount tears all of it down;
// componentDidUpdate reconciles prop/state transitions (instance swap,
// spectate, region view). The board tutorial autoplay scheduling and
// the window-level click tracker also live here.
//
// Host contract: the full GameBoard surface; these functions are the
// composition root that wires every other controller module together.

import { isDevBuild } from '../../../utils/devMode';
import { installTestBridge } from './testBridge';
import { onViewportScaleChange } from '../../../lib/medievalTheme';
import { getAmbienceState, playAmbience, stopAmbience, subscribeAmbience } from '../../../utils/arena/ambienceManager';
import { getCosmeticsCached } from '../../../utils/arena/cosmeticsCache';
import { enterSubsurfaceAmbience, exitSubsurfaceAmbience, setSubsurfaceMix, stopSubsurfaceAmbience } from '../../../utils/arena/subsurfaceAmbience';
import { hydrateTutorialState, markTutorialSeen, shouldAutoPlay } from '../../../utils/arena/tutorialState';
import { applyReplayViewpoint, replayViewpointChanged } from './replayViewpoint';
import { UI } from '../../../utils/arena/uiSounds';
import { onAccessibilityChange } from '../../../utils/game/accessibilitySettings';
import { onControlSettingsChange } from '../../../utils/game/controlSettings';
import { applyPositionsFromMirror } from './authZones';
import { disposeTextureCache, onPillBgReady, setCardBackUrls, updateCounterHUD, updateLifeHUD } from '../../../utils/game/cardMesh';
import { disposeDiceMesh, tickDice } from '../../../utils/game/diceMesh';
import { redrawDiceArtwork } from '../../../utils/game/dice3d/dieAssets';
import { announceDicePalette, followDicePalette } from './dicePaletteSync';
import { clearHoveredCard as clearZoomBusHoveredCard, setHoveredCard as setZoomBusHoveredCard } from '../../../utils/game/cardZoomBus';
import { GridEditor } from '../../../utils/game/gridEditor';
import { GridRenderer } from '../../../utils/game/gridRenderer';
import { MatchConnection } from '../../../utils/game/net/MatchConnection';
import { isAuthMatchEnabled } from '../../../utils/game/net/authMatchFlag';
import { shouldArmFirstPlayerDice } from '../../../utils/game/net/restoreHelpers';
import { createPhysicsWorld, stepPhysics } from '../../../utils/game/physicsWorld';
import { createPingLayer } from '../../../utils/game/pingLayer';
import { createLightningLayer } from '../../../utils/game/lightningLayer';
import { PlaymatComponentEditor } from '../../../utils/game/playmatComponentEditor';
import { PlaymatComponentRenderer } from '../../../utils/game/playmatComponentRenderer';
import { loadGameSessionIfExists } from '../../../utils/game/sessionStorage';
import { disconnectSocket, requestMatchFull, setMatchSeatProvider } from '../../../utils/game/socketClient';
import { preloadSounds } from '../../../utils/game/sounds';
import { getGameGrid, loadSpawnConfig, setGameGrid } from '../../../utils/game/spawnConfig';
import { createTableScene } from '../../../utils/game/tableScene';
import { UiInversionEditor } from '../../../utils/game/uiInversionEditor';
import { UiInversionRenderer } from '../../../utils/game/uiInversionRenderer';
import { COMPONENT_PLACEMENTS, getDefaultComponentPlacements } from '../../../utils/game/uiOverlayGenerator';
import { isControllerEnabled } from '../../../utils/input/controllerFeature';
import { getLocalApiOrigin } from '../../../utils/localApi';
import { perf } from '../../../utils/perfMonitor';
import { onCanvasScaleChange } from '../../../utils/uiScale';
import { getCurrentPlaymatInversionRegions, getPlaymatUrl, getVanillaPlaymatUrl, prewarmPlaymatPreviews } from '../../PlaymatPicker';
import { BOARD_TUTORIAL_KEY } from '../boardTutorialSteps';
import { completeEventTutorialOnAction } from './eventTutorials';
import { t } from '../../../localization/t';

export function handleBoardTutorialDismiss(host) {
  const profileId = host.props.profile?.id;
  if (profileId) markTutorialSeen(profileId, BOARD_TUTORIAL_KEY);
  host.setState({ showBoardTutorial: false, tutorialOpenStatusTokens: false });
}

export function handleBoardTutorialStepChange(host, stepKey) {
  const open = stepKey === 'status-tokens';
  if (host.state.tutorialOpenStatusTokens !== open) {
    host.setState({ tutorialOpenStatusTokens: open });
  }
}

// Queue a deferred re-check of the auto-play conditions. Callers
// fire this whenever something that could unblock the tutorial
// changes (hydrate resolves, online-match determination lands,
// first-player dice closes). The timer collapses repeated calls
// so we never stack multiple pending shows.
export function scheduleBoardTutorialCheck(host, delayMs) {
  if (host._unmounted) return;
  if (host._boardTutorialTimer) clearTimeout(host._boardTutorialTimer);
  host._boardTutorialTimer = setTimeout(() => {
    host._boardTutorialTimer = null;
    host._maybeAutoPlayBoardTutorial();
  }, Math.max(0, delayMs));
}

// Whether this mount may auto-play the board walkthrough at all. A
// replay or live spectate is watched, not played: the tutorial's "play a
// card, open the ring menu" steps have nothing to act on, and a fresh
// account watching a shared replay got the whole walkthrough over the
// replay controls (QA#149). Same gate the one-shot event tutorials apply.
export function boardTutorialEligible(host) {
  return !!host.props.profile?.id && !host.props.isSpectating;
}

// Show the board tutorial iff (a) hydrate decided we should
// auto-play it, and (b) the first-player dice modal isn't holding
// (or about to hold) the screen. Otherwise leave _wantsBoardTutorial
// set and let the dice-close path retry us.
export function maybeAutoPlayBoardTutorial(host) {
  if (host._unmounted) return;
  if (!host._wantsBoardTutorial) return;
  if (host.state.showFirstPlayerDice || host._diceModalPending) return;
  // Hold the tutorial behind an open deck picker so the spotlight isn't
  // fighting a modal that covers the board.
  if (host.state.showDeckPicker) return;
  host._wantsBoardTutorial = false;
  host.setState({ showBoardTutorial: true });
}

// Gated first-player dice opener. The single funnel for auto-opening the dice
// so the 1s mount timer AND the durable-snapshot resolution (adopt / 'none' /
// timeout) all drive it through one guard (shouldArmFirstPlayerDice). Bails
// while a server save is still pending so a reconnect restores the real board
// instead of popping the dice over it.
export function maybeArmFirstPlayerDice(host) {
  if (host._unmounted) return;
  if (host.state.showFirstPlayerDice) return;
  // Auth: the MIRROR is the phase truth. Arm the dice only once the server's
  // state has landed AND it says the roll-off is genuinely unresolved. The
  // client's own rollState can be a cold-mount default (nulls), and arming
  // off it forced a mid-match reload back to 'rollOff' on both clients. A
  // fresh match arms from the fold pipeline the moment the Full lands.
  if (host._authZonesEnabled?.()) {
    const m = host._authMirror?.getState?.();
    if (!m?.phase?.matchPhase) return; // mirror not landed yet. The fold re-arms
    if (m.phase.matchPhase !== 'rollOff') return;
    if (m.rollOff?.firstPlayerSeat) return;
    // matchPhase stays 'rollOff' through the whole start choreography, so it
    // alone doesn't mean the roll is unresolved. The authoritative rolling
    // sub-step is startStep 'roll'; once the server has advanced past it
    // (preGame / openingDraw / done) the modal is stale. Never re-arm it (the
    // reconnect "dice modal flashes over a decided roll" bug). A fresh match
    // sits at startStep 'roll', so this still arms the modal normally.
    if (m.phase.startStep && m.phase.startStep !== 'roll') return;
  }
  if (!shouldArmFirstPlayerDice({
    gameStarted: host.state.gameStarted,
    firstPlayerSeat: host.state.firstPlayerRollState?.firstPlayerSeat,
    authSnapshotPending: host._authSnapshotPending,
  })) return;
  host.setState(
    { showFirstPlayerDice: true, matchPhase: 'rollOff' },
    () => host.autoSave({ force: true }).catch(() => {}),
  );
}

export function onWindowClick(host, e) {
  // Outside-click dismissal for HUD buff / life panels. The HUD
  // rows and the panels themselves carry data-buff-zone, so we
  // walk up the target's ancestors looking for any such marker.
  // if none found, close any open panel.
  if (!host.state.hudPanel) return;
  let node = e.target;
  while (node && node !== document.body) {
    if (node.dataset?.buffZone) return;
    node = node.parentNode;
  }
  host._closeHudPanel();
}

export async function componentDidMount(host) {
  window.addEventListener('mousedown', host._onWindowClick);
  // Pre-load the 8 small (~30 KB each) playmat preview thumbnails
  // into the renderer's module-level blob cache so the playmat
  // editor + cosmetics store playmat tab open instantly the first
  // time the player visits either. Fire-and-forget. Failures fall
  // back to the on-demand fetch path.
  prewarmPlaymatPreviews?.();
  const canvas = host.canvasRef.current;
  if (!canvas) return;

  // Devtools hooks for diagnosing the stack architecture. Call
  // `_dumpStacks()` in the console to print every card's parent
  // and chain depth; toggle `_logStacking` to get per-event logs
  // from the resolver / detach paths.
  if (typeof window !== 'undefined') {
    window._dumpStacks = host._dumpStacks;
    if (window._logStacking === undefined) window._logStacking = false;
    // Dev-only E2E test bridge (window.__test). Lets the Playwright
    // harness drive this sandbox match through real host methods.
    if (isDevBuild()) installTestBridge(host);
  }

  host.physicsWorld = await createPhysicsWorld();
  preloadSounds();

  // Solo match: every outgoing match command/intent names its acting seat on
  // the wire envelope (the pilot drives BOTH seats over one socket). The
  // provider resolves the seat contextually (see GameBoard._soloActingSeat).
  if (host.props.isCasualSandbox) {
    setMatchSeatProvider((msg, kind) => host._soloActingSeat(msg, kind));
  }

  // Warm the cosmetics cache so the Token Browser can read owned
  // custom-art tokens synchronously when the player opens it. The
  // cache is shared with the store / playmat picker. Usually warm
  // by the time we get here, but harmless if it isn't.
  //
  // The repaint afterwards is the dice skins' self-correction: an
  // unowned skin renders while the cache is cold (getDicePalette fails
  // open), and only a redraw takes it back. Nothing changes in the
  // common warm case, where the entitlement already resolved the same
  // way when the atlas was first cut.
  getCosmeticsCached().then(() => redrawDiceArtwork()).catch(() => {});

  host.unsubAmbience = subscribeAmbience(() => {
    host.setState({ ambienceState: getAmbienceState() });
  });
  host.unsubScale = onViewportScaleChange((scale) => host.setState({ viewScale: scale }));
  host.unsubDicePalette = followDicePalette(host);
  // The Full may have landed before the board was ready to speak for its
  // seat (the solo seat provider is installed just above); say it now, and
  // again on every Full. The server ignores a palette it already holds.
  announceDicePalette(host, host._authMirror?.getState?.());
  // Auto-start ambience when entering the game board.
  playAmbience();

  // When the pill background WebPs finish decoding, re-bake any
  // already-rendered HUDs so a card spawned before the textures
  // were ready doesn't get stuck on the gradient-only fallback.
  host.unsubPillBg = onPillBgReady(() => {
    for (const [cardId, hud] of host.lifeHUDs) {
      const mesh = host.meshes.get(cardId);
      const card = mesh?.userData?.cardInstance;
      if (!card) continue;
      // Sorcery "power" = ATK + HP combined; aura delta applies to
      // both halves of a minion's stat line.
      const auraDelta = card._auraStatDelta || 0;
      const atk = (card.currentAttack ?? 0) + auraDelta;
      // Floor HP display at 0. CurrentLife is unclamped so 0-base
      // minions can register damage for the death sweep, but the
      // HUD shouldn't show negative numbers.
      const hp = Math.max(0, card.currentLife ?? 0) + auraDelta;
      updateLifeHUD(hud.sprite, atk, 'atk', atk - (card.baseAttack ?? atk));
      if (hud.hpSprite) updateLifeHUD(hud.hpSprite, hp, 'hp', hp - (card.baseLife ?? hp));
    }
    // Counter tokens share the same stone/scratches surface. Re-bake
    // any that rendered before the WebPs decoded.
    for (const [cardId, token] of host.counterHUDs) {
      const card = host.meshes.get(cardId)?.userData?.cardInstance;
      if (card) updateCounterHUD(token, card._counters || 0);
    }
  });

  const apiOrigin = getLocalApiOrigin();
  setCardBackUrls(
    `${apiOrigin}/game-assets/cardback-spellbook-rounded.png`,
    `${apiOrigin}/game-assets/cardback-atlas-rounded.png`
  );

  try {
    const savedPlaymat = getPlaymatUrl();
    // The blurred floor background mirrors the active playmat's art
    // (same as setPlaymat → updateBackground does on a manual change).
    // Seed it from the saved mat's art on load too, otherwise a
    // restored game shows the generic table background until the
    // player changes mats. Falls back to the default backdrop only if
    // a mat has no art URL.
    const savedArtUrl = typeof savedPlaymat === 'string' ? savedPlaymat : savedPlaymat?.art;
    const backgroundUrl = savedArtUrl || `${apiOrigin}/game-assets/table-background-hd.png`;
    host.scene = createTableScene(canvas, savedPlaymat, backgroundUrl);
    // Replay: the feed sidebar covers the left ~25.5cqw, so the board
    // centers (slightly zoomed out) in the visible area beside it.
    if (host.props?.replayDriver) host.scene.setViewInsetLeft?.(0.255);
    // Preload the vanilla playmat texture so the subsurface
    // crossfade is GPU-only when toggled.
    host.scene.setVanillaPlaymat?.(getVanillaPlaymatUrl());
  } catch (err) {
    console.error('[GameBoard] Failed to create 3D scene:', err);
    host.setState({
      connectionFailed: true,
      loadingMessage: t('Failed to initialize 3D renderer'),
    });
    return;
  }

  if (!host.scene) {
    console.error('[GameBoard] Scene creation returned null');
    host.setState({
      connectionFailed: true,
      loadingMessage: t('Failed to initialize 3D renderer'),

    });
    return;
  }

  host.pingLayer = createPingLayer(host.scene.scene, host.scene);
  host.lightningLayer = createLightningLayer(host.scene.scene, host.scene, {
    renderer: host.scene.renderer, camera: host.scene.camera,
  });

  host.gridRenderer = new GridRenderer(host.scene);
  host.gridEditor = new GridEditor({
    renderer: host.gridRenderer,
    getGrid: () => getGameGrid(host.state.spawnConfig),
    onChange: (grid) => {
      const newConfig = { ...host.state.spawnConfig };
      setGameGrid(newConfig, grid);
      host.setState({ spawnConfig: newConfig });
    },
    onCommit: (grid) => {
      const newConfig = { ...host.state.spawnConfig };
      setGameGrid(newConfig, grid);
      host.setState({ spawnConfig: newConfig });
    },
  });

  // Dev-only UI-inversion editor. Mirrors GridEditor exactly:
  // outline + handles on the 3D mat, drag-to-define new rect, drag
  // handle to resize, drag region body to move. Every region
  // mutation pipes straight into the shader uniform so what the
  // dev drags is what they see at render time.
  host.uiInversionRenderer = new UiInversionRenderer({
    scene: host.scene,
    uvToWorld: (uv) => host.scene.uvToWorld(uv),
  });
  host.uiInversionEditor = new UiInversionEditor({
    renderer: host.uiInversionRenderer,
    getInitialRegions: () => getCurrentPlaymatInversionRegions(),
    onChange: (regions) => {
      host.scene?.setUiInvertRegions?.(regions);
    },
  });

  // Dev-only playmat component editor. Renders the 12 components
  // (atlas/spellbook/cemetery × 2 + life/mana/affinity trackers
  // × 2) as draggable 3D meshes. Drag positions update in real
  // time. On open: tells tableScene to skip baking those
  // components into the UI texture; on close: reinstates the bake.
  host.playmatComponentRenderer = new PlaymatComponentRenderer(host.scene);
  host.playmatComponentEditor = new PlaymatComponentEditor({
    renderer: host.playmatComponentRenderer,
    getInitialPlacements: () => ({
      ...getDefaultComponentPlacements(),
      ...COMPONENT_PLACEMENTS,
    }),
    onChange: () => {
      // Re-render the popover so the displayed count / active
      // placement coords reflect the in-flight drag state.
      host.forceUpdate();
    },
  });

  canvas.addEventListener('mousedown', host.handleMouseDown);
  canvas.addEventListener('mousemove', host.handleMouseMove);
  canvas.addEventListener('mouseup', host.handleMouseUp);
  canvas.addEventListener('contextmenu', host.handleContextMenu);

  // Listen for a mouseup ANYWHERE on the window so a marquee drag
  // that leaves the canvas edge still closes cleanly. Same for
  // Escape. It cancels an active marquee or clears selection.
  window.addEventListener('mouseup', host.handleWindowMouseUp);
  window.addEventListener('keydown', host.handleWindowKeyDown);
  window.addEventListener('keyup', host.handleWindowKeyUp);
  window.addEventListener('blur', host.handleWindowBlur);

  // Controller / gamepad wiring. Fully gated on the feature flag.
  if (isControllerEnabled()) host.initController();

  window.addEventListener('resize', host.handleResize);
  host.handleResize();
  // Keep the hand sized in lockstep with the cqw UI: re-render whenever
  // the canvas scale changes (window resize or aspect-pref change).
  host.unsubCanvasScale = onCanvasScaleChange((canvasScale) => host.setState({ canvasScale }));
  // Create 3D player badges after the scene is ready.
  host.updatePlayerBadges();

  // Per-frame loop. Three responsibilities:
  //   1. Step the local physics world
  //   2. For each card mesh, drive the body either from local physics
  //      (local-owned or free) or from the remote interpolation buffer
  //      (remote-owned), then sync the result onto the mesh
  //   3. Stream pose updates for locally-owned cards, auto-claim free
  //      cards whose body just woke (cascade detection), and release
  //      ownership when bodies sleep
  perf.start();
  host.unsubFrame = host.scene.onFrame((dt) => {
    const frameMark = perf.beginMark('frame.tick');
    const physicsMark = perf.beginMark('frame.physics');
    stepPhysics(host.physicsWorld, dt);
    perf.endMark(physicsMark);
    const syncMark = perf.beginMark('frame.sync');
    host.tickPhysicsSync();
    // Override every non-dragged card's Y with the computed stack-
    // rank position. The "most-recently-touched is on top" rule lives
    // here: physics-driven Y is irrelevant after this pass.
    host._applyComputedY();
    // v1.72. Region view: surface vs subsurface. Hides above-ground
    // units in subsurface view so burrowed/submerged cards stand
    // out. Cheap per-frame visibility flip; no-op when in the
    // default surface mode.
    host._applyRegionViewVisibility();
    // Dice ride their cards, so they go after the cards have moved.
    tickDice(host.diceMeshes.values(), dt, host._diceContext());
    host._diceTrayRig?.tick(dt);
    perf.endMark(syncMark);
    // Hug rect follows the selected cards every frame so it tracks
    // drags, settle animations, and camera pans. Early-outs inside
    // updateHugRect() make the no-selection case a single Set size
    // check per frame.
    host.updateHugRect();
    host.updateStartDrawOverlays();
    host.updateHoverStatusOverlayPosition();
    host.updateEquipPendingOverlays();
    host._syncTargetHaloMeshes();
    host.updateCellTargetOverlays();
    host.updateCellMentionOverlays();
    host.updateProjectilePathOverlays();
    host.updateMovementReachOverlays();
    host.updateInvalidCellHoverOverlay();
    host.updateCellElementPipOverlays();
    host.updateCellStackBadgeOverlays();
    // Cell → fan → act: advance the cosmetic XZ glides (fan out/in, cascade
    // re-slots, confirmed moves) and re-project the hover highlight.
    host._tickPoseGlides();
    host.updateCellHoverHighlight();
    host.updateCellFanChrome();
    host._syncRegionViewTints();
    host._syncSubsurfaceSceneState();
    host._syncHarbingerCircles();
    host._syncDragonlordReadyGlow(dt);
    host._syncHowlAuras(dt);
    host._syncDragonSpiritEchoes(dt);
    host.updateEquipmentOverlaysPosition();
    host._tickCardEffects(dt);
    // Board reconcile (batched): enforce scene = f(card state) once per
    // dirty frame, and keep advancing the exit deadline while any mesh is
    // mid-departure so a stranded fly-to-pile can never linger as a ghost.
    if (host._boardReconcileDirty || host._exitingMeshes?.size) {
      host._boardReconcileDirty = false;
      host._reconcileBoardMeshes();
    }
    perf.endMark(frameMark);
  });

  loadSpawnConfig().then((config) => {
    host.setState({ spawnConfig: config }, () => {
      host.createTrackerTokens();
      host.createTrackerButtons();
    });
  });

  // First-run onboarding. Deferred a beat so the hand / HUD have
  // laid out before the overlay measures its targets, and so the
  // user sees the table before the tutorial modal appears. Awaits
  // tutorialState hydration so the seen flag from disk is read
  // before we decide whether to show the overlay.
  //
  // For online / ranked matches the first-player dice modal also
  // wants the screen; we set _diceModalPending synchronously so
  // _maybeAutoPlayBoardTutorial holds the tutorial behind the
  // dice. initSession refines the flag once it knows for certain
  // whether this session will actually roll. _resolveFirstPlayerRoll
  // re-fires the check after the dice closes.
  host._diceModalPending = !!(
    host.props.isArenaMatch || host.props.arenaSelectedDeckId
  );
  const profileId = host.props.profile?.id;
  if (boardTutorialEligible(host)) {
    hydrateTutorialState().then(() => {
      if (host._unmounted) return;
      if (!shouldAutoPlay(profileId, BOARD_TUTORIAL_KEY)) return;
      host._wantsBoardTutorial = true;
      host._scheduleBoardTutorialCheck(900);
    });
  }

  host.initSession().then(async () => {
    // Spectators must NOT run any of the player-only setup that follows
    // (spawn-decks, FirstPlayerDice modal, opening-draw, auto-saves). The
    // spectator-projected match:full pulled in spectate's initSession
    // branch is what populates their board. Without this guard the
    // spectator gets the dice modal popped over a phantom opponent
    // and the spectated state gets stamped into any stale save
    // scope inherited from a prior match.
    if (host.props.isSpectating) {
      host._diceModalPending = false;
      // Spectators render BOTH seats' hidden piles from the counts the
      // spectate room:joined payload carries (identity-free stacks + the
      // public avatars); board meshes and live updates then fold from the
      // spectator-projected match:full/diff stream.
      const { roomInfo } = host.state;
      try {
        if (roomInfo?.hostDeckCounts) host._spawnOpponentCounts(roomInfo.hostDeckCounts, 1, roomInfo.hostAvatar);
        if (roomInfo?.guestDeckCounts) host._spawnOpponentCounts(roomInfo.guestDeckCounts, 2, roomInfo.guestAvatar);
      } catch (err) {
        console.error('Failed to spawn spectator count stacks:', err);
      }
      setTimeout(() => {
        if (!host._unmounted) host.setState({ isLoading: false });
      }, 500);
      return;
    }
    // Ranked matchmaking: the server provides BOTH decks on room:joined,
    // so every client spawns identical, authoritative initial state.
    // No client-to-client races possible.
    const { roomInfo } = host.state;
    // Instance-resume paths: host re-entered a saved arena match
    // (sessionMode='load') or guest was flagged by the server's
    // phaseTransition as resumed. Both skip the fresh-match spawn and
    // opening draw. The board rebuilds from the authoritative Full the
    // connection pulls on resume (requestFullWithRetry in MatchConnection).
    const isHostResume = host.props.sessionMode === 'load' && host.props.isArenaMatch;
    const isGuestResume = !!host.props.isResumeJoin;
    // Draft round resume: a local blob scoped to this round's matchId
    // (see _instanceSaveId) marks the round as in-progress. The blob is
    // only a DETECTOR now. The board itself rebuilds from the server's
    // authoritative Full, never from the stale local copy.
    let isDraftRoundResume = false;
    if (!isHostResume && !isGuestResume && host.props.draftMatchId) {
      const saveId = host._instanceSaveId();
      if (saveId) {
        try {
          const blob = await loadGameSessionIfExists(saveId);
          if (blob) {
            isDraftRoundResume = true;
            host.requestAuthoritativeState();
          }
        } catch (err) {
          console.warn('[GameBoard] draft round resume check failed:', err);
        }
      }
    }
    if (isHostResume || isGuestResume) {
      // The connection's resume path already pulled the Full; nothing to
      // restore locally.
    } else if (isDraftRoundResume) {
      // Handled above. Skip the fresh-deck-spawn path below.
    } else if (host.props.isArenaMatch && (roomInfo?.hostDeck || roomInfo?.guestDeck)) {
      // R2b. True hidden info: the server now ships only the LOCAL seat's deck
      // (the opponent's contents are withheld; counts arrive as
      // `roomInfo.opponentDeckCounts`). The legacy path still ships both full
      // decks; the branch keys off the LOCAL deck being present so both work.
      // Reconnection case: the server actor holds the match. Pull the
      // authoritative Full instead of re-spawning fresh decks. The board
      // rebuilds from that Full, but MatchAssetGate still waits on the
      // opponent's deck to run phase 2, so hand it over from roomInfo (solo
      // and legacy rooms carry both decks) or the loading overlay never
      // clears and the player is stuck on "Loading Opponent" forever.
      if (roomInfo.resumed) {
        resolveResumedOpponentDeck(host, roomInfo);
        host.requestAuthoritativeState(roomInfo.roomCode);
      } else {
        // Local deck first. Its art was preloaded by MatchAssetGate's
        // phase 1 before this component mounted, so the spawn is
        // guaranteed to hit the disk cache. Opponent's deck is queued
        // until the gate finishes phase 2 (props.assetsReady flips
        // true via _spawnQueuedOpponent in componentDidUpdate). Without
        // that gate, the texture loader would race the gate's download
        // and bake transparent cards into the live scene.
        try {
          const localIsHost = host.state.isHost;
          const localDeck = localIsHost ? roomInfo.hostDeck : roomInfo.guestDeck;
          const opponentDeck = localIsHost ? roomInfo.guestDeck : roomInfo.hostDeck;
          const opponentPlayerNum = localIsHost ? 2 : 1;
          host.sync.withSuppressed(() => {
            host.spawnSelectedDeck(localDeck, localIsHost ? 1 : 2);
          });
          // Auth mode: the opponent's deck contents never arrive. Render its
          // piles from counts only (identity-free face-down stacks). No asset
          // gate needed (placeholders have no art). Solo: the pilot owns BOTH
          // full decks (no counts on room:joined) so the guest deck spawns
          // fully via the queued own-deck path below. Legacy mode: queue the
          // full opponent deck behind the gate as before.
          if (isAuthMatchEnabled() && roomInfo.opponentDeckCounts) {
            host._spawnOpponentCounts(roomInfo.opponentDeckCounts, opponentPlayerNum, roomInfo.opponentAvatar);
          } else if (opponentDeck) {
            host._queueOpponentSpawn(opponentDeck, opponentPlayerNum);
          }
        } catch (err) {
          console.error('Failed to spawn match decks:', err);
        }
      }
    } else if (host.props.arenaSelectedDeckId) {
      // Non-matchmaking arena (e.g. friend invite). Fall back to the
      // legacy single-deck flow. TODO: migrate invite flow to server-auth too.
      const localPlayerNum = host.state.isHost ? 1 : 2;
      try {
        await host.doSpawnDeck(host.props.arenaSelectedDeckId, localPlayerNum);
      } catch {}
      if (!host.state.isHost && host.props.sessionMode === 'join') {
        host.requestAuthoritativeState();
      }
    } else if (roomInfo?.resumed) {
      // Rejoin path with no deckId prop (app restart via session:active).
      // the server actor is the only restore source.
      host.requestAuthoritativeState(roomInfo.roomCode);
    }
    // Solo (one pilot, both seats): pull the authoritative Full up front.
    // A FRESH solo match arms the roll-off dice from this fold (the mirror
    // is the phase truth); a RESUMED one always mounts sessionMode 'new'
    // into a re-minted room, so this same Full is what rebuilds the board,
    // both hands and both pile orders over the freshly spawned decks
    // (the dual-own-seat reconnect folds).
    if (host.props.isCasualSandbox && isAuthMatchEnabled() && host._matchKey()) {
      requestMatchFull(host._matchKey());
    }
    // Determine whether this session should auto-draw opening hands.
    // Only for fresh competitive or friend matches. Not solo/offline,
    // not reconnections (resumed), not loaded saved sessions.
    const isOnlineMatch = !isHostResume && !isGuestResume && !isDraftRoundResume && (
      (host.props.isArenaMatch && roomInfo && !roomInfo.resumed)
      || (host.props.arenaSelectedDeckId && host.state.connectionStatus !== 'offline')
    );

    // Refine the synchronous over-approximation set in
    // componentDidMount. If the session won't actually roll dice,
    // unblock the tutorial; if it will, leave the flag set so
    // _resolveFirstPlayerRoll fires the check after the modal closes.
    host._diceModalPending = !!isOnlineMatch;
    if (!isOnlineMatch) host._scheduleBoardTutorialCheck(0);

    // Give textures a moment to upload to GPU
    setTimeout(() => {
      host.setState({ isLoading: false });

      // 1 second after the board appears, show the dice roll modal
      // and gate the opening draw on its result. The modal handles
      // host vs guest sync internally; the resolver derives what
      // to draw from props (sandbox = both seats, online = local
      // seat) so no closure needs to live on state. Flipping
      // matchPhase to 'rollOff' here force-saves immediately so a
      // close during the modal resumes back into it.
      if (isOnlineMatch) {
        // Arm the first-player dice 1s after the board appears. But only via
        // the gated opener. maybeArmFirstPlayerDice bails when the roll has
        // resolved, the game is underway, OR a durable auth snapshot is still
        // in flight (a reconnect/app-restart pulls the server save first;
        // popping the dice before it lands clobbers matchPhase to 'rollOff'
        // over the real game. The reload "initiative modal + wiped board"
        // bug). The snapshot adopt / 'none' reply re-invokes this check.
        setTimeout(() => host._maybeArmFirstPlayerDice(), 1000);
      }
    }, 500);
  });
  host.autoSaveTimer = setInterval(() => host.autoSave(), 60000);
  // Combat liveness loop. Heals a durable combat record that diverged from the
  // host's engine (a dropped terminal frame) and re-sends an unconsumed
  // decision, so a single lost combat frame can never permanently strand a
  // client. No-op in sandbox and when no fight is in flight.
  host._combatReconcileTimer = setInterval(() => host._combatReconcileTick(), 2000);
  // For online matches, save our full local state every few seconds so a
  // rejoining player can restore their hand (which the opponent doesn't
  // have the card IDs for). Spectators never persist. Their board is
  // a mirror of the host's state and would otherwise pollute the
  // spectated room's localStorage entry.
  if (host.props.isArenaMatch && !host.props.isSpectating) {
    host.localSaveTimer = setInterval(() => host.scheduleLocalSave(), 3000);
  }

  // Subscribe to accessibility settings so toggling the ownership
  // outline (or swapping colors / thickness) takes effect without
  // needing to re-enter the match.
  host._unsubAccessibility = onAccessibilityChange(() => host.refreshOwnershipOutlines());

  // Toggling "Snap Cards to Squares" mid-match re-lays the whole board from
  // the mirror: on → every stored pos yields to the cell cascade, off → cards
  // with stored coords glide back to where they were dropped.
  host._unsubControlSettings = onControlSettingsChange(() => {
    const mirror = host._authMirror?.getState?.();
    if (mirror) applyPositionsFromMirror(host, mirror);
  });
}

// Resume rejoin (room:resumed): the fresh-spawn path is skipped, so the
// only live producer of the gate's opponentDeck prop (_queueOpponentSpawn)
// never runs. Deliver the opponent's deck straight from roomInfo when the
// payload carries it. When it doesn't (part-created room), the gate stays
// up and the escape hatch is the way out.
export function resolveResumedOpponentDeck(host, roomInfo) {
  const opponentDeck = host.state.isHost ? roomInfo?.guestDeck : roomInfo?.hostDeck;
  if (opponentDeck) host.props.onOpponentDeckResolved?.(opponentDeck);
  return opponentDeck || null;
}

export function componentWillUnmount(host) {
  host._unmounted = true;
  setMatchSeatProvider(null);
  // Abort any in-flight guest join retry loop owned by MatchConnection.
  host.conn?.abortJoin();
  host._trackerLogCoalescer?.dispose();
  window.removeEventListener('mousedown', host._onWindowClick);
  clearZoomBusHoveredCard();
  const canvas = host.canvasRef.current;
  if (canvas) {
    canvas.removeEventListener('mousedown', host.handleMouseDown);
    canvas.removeEventListener('mousemove', host.handleMouseMove);
    canvas.removeEventListener('mouseup', host.handleMouseUp);
    canvas.removeEventListener('contextmenu', host.handleContextMenu);
  }
  window.removeEventListener('resize', host.handleResize);
  host.unsubCanvasScale?.();
  window.removeEventListener('mouseup', host.handleWindowMouseUp);
  window.removeEventListener('keydown', host.handleWindowKeyDown);
  window.removeEventListener('keyup', host.handleWindowKeyUp);
  window.removeEventListener('blur', host.handleWindowBlur);
  if (isControllerEnabled()) host.teardownController();
  clearTimeout(host.handRetractTimer);
  // Tear down any in-flight Howl reveal (temp meshes + auras + timers).
  host._clearHowlScene?.();
  if (host._boardTutorialTimer) clearTimeout(host._boardTutorialTimer);
  if (host._eventTutorialTimer) clearTimeout(host._eventTutorialTimer);
  host._clearAllPendingMoves?.();
  host._clearAllPendingCasts?.();
  clearInterval(host.autoSaveTimer);
  clearInterval(host.localSaveTimer);
  clearInterval(host._combatReconcileTimer);
  clearTimeout(host._localSaveTimer);
  clearInterval(host._disconnectCountdown);
  // Spectators never write a save. Their state is the spectated
  // host's view, not their own. Without this guard a stale
  // instanceId/draftMatchId inherited from a prior match would
  // give autoSave a save scope and stamp the spectated state into
  // the user's local blob, which the next Play Match resumes from.
  if (!host.props.isSpectating) host.autoSave();
  if (host.unsubAmbience) host.unsubAmbience();
  if (host.unsubScale) host.unsubScale();
  if (host.unsubDicePalette) host.unsubDicePalette();
  if (host.unsubPillBg) host.unsubPillBg();
  if (host._serverReconnectUnsub) host._serverReconnectUnsub();
  if (host._unsubAccessibility) host._unsubAccessibility();
  if (host._unsubControlSettings) host._unsubControlSettings();
  stopSubsurfaceAmbience();
  stopAmbience({ silent: true });
  host._disposeRegionViewTints();
  host._disposeHarbingerCircles();
  host._disposeDragonlordReadyGlows();
  host._disposeDragonSpiritEchoes();
  host._harbingerSweepCancel?.();
  host._harbingerSweepCancel = null;
  // Unsubscribe every game action handler before tearing down the socket.
  host.sync.destroy();
  perf.stop();
  if (host.unsubFrame) host.unsubFrame();
  disconnectSocket();
  for (const cardId of Array.from(host.cardEffects.keys())) {
    host._removeAllCardEffects(cardId);
  }
  host.meshes.clear();
  host.pileMeshes.clear();
  host.tokenMeshes.clear();
  host.lifeHUDs.clear();
  for (const [, mesh] of host.diceMeshes) disposeDiceMesh(mesh);
  host.diceMeshes.clear();
  for (const [, mesh] of host.trackerTokenMeshes) {
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m?.dispose());
  }
  host.trackerTokenMeshes.clear();
  for (const [, mesh] of host.trackerButtonMeshes) {
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m?.dispose());
  }
  host.trackerButtonMeshes.clear();
  host.trackerPreviewMarkers = [];
  for (const mesh of host.opponentHandMeshes) {
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m?.dispose());
  }
  host.opponentHandMeshes = [];
  host._disposeBadge(host._playerBadgeMesh);
  host._disposeBadge(host._opponentBadgeMesh);
  host._playerBadgeMesh = null;
  host._opponentBadgeMesh = null;
  disposeTextureCache();
  host.pingLayer?.dispose();
  host.pingLayer = null;
  host.lightningLayer?.dispose();
  host.lightningLayer = null;
  host.gridRenderer?.dispose();
  host.gridRenderer = null;
  host.gridEditor = null;
  host.uiInversionRenderer?.dispose();
  host.uiInversionRenderer = null;
  host.uiInversionEditor = null;
  host.playmatComponentRenderer?.dispose();
  host.playmatComponentRenderer = null;
  host.playmatComponentEditor = null;
  host.scene?.dispose();
  host.scene = null;
}

export function componentDidUpdate(host, prevProps, prevState) {
  // Mandatory event tutorials complete only by performing the taught
  // action (ring menu opened, cell fan opened). Detected here so every
  // input path counts.
  completeEventTutorialOnAction(host, prevState);

  // Replay: the viewer switched sides. Flip the seat in place; the board
  // is not remounted for it (QA#151).
  if (replayViewpointChanged(host, prevProps)) {
    applyReplayViewpoint(host, host.props.replayViewpoint);
  }

  // Focus the save-dialog input imperatively each time the dialog
  // opens. `autoFocus` only fires reliably on the first mount per
  // document (Chromium one-shot); without this, reopening the dialog
  // leaves the smooth caret with no focus event to track.
  if (!prevState.showSaveDialog && host.state.showSaveDialog) {
    host.saveDialogInputRef?.current?.focus();
  }

  // A sandbox turn pass parked behind the outgoing player's prompts
  // resumes the moment its blockers clear. Observed HERE, at the one
  // chokepoint every state change passes through, because prompts are
  // removed from ~14 different resolution sites and only a few of them
  // remember to call _maybeResumeDeferredTurnPass directly. Without
  // this, a blocker resolved through any other path left the deferred
  // pass latched forever and End Turn silently dead.
  if (host._deferredTurnPass
      && ((prevState.pendingPrompts?.length || 0) > 0 || prevState.pendingCellTarget)
      && (host.state.pendingPrompts?.length || 0) === 0
      && !host.state.pendingCellTarget) {
    host._maybeResumeDeferredTurnPass();
  }

  // Gate flushed phase 2. Any opponent spawn we held back is now
  // safe to apply. Fires once per assetsReady transition.
  if (host.props.assetsReady && !prevProps.assetsReady) {
    host._spawnQueuedOpponent();
  }

  // Accessibility outlines are painted when each card is spawned,
  // but `isOwnedByLocalPlayer` reads both `connectionStatus` and
  // `isHost`. And during match handshake those default values
  // (`'offline'` / `true`) can still be in place when the first
  // cards land on the table, causing every card to be classified
  // as "own" via the offline shortcut. Repaint whenever either
  // transitions so the ring colors catch up with the now-authoritative
  // match state.
  if (prevState.isHost !== host.state.isHost) {
    // Seat resolved (or flipped). Re-apply the whole perspective so the
    // badges/camera catch up with the authoritative seat, not just the
    // ownership outlines. This is the seat-mirror fix: previously only
    // refreshOwnershipOutlines() ran here, leaving the 3D badges stranded.
    // Spectators keep the default orientation, so they only repaint outlines.
    if (host.props.isSpectating) host.refreshOwnershipOutlines();
    else host.applySeatPerspective(host.state.isHost);
  } else if (prevState.connectionStatus !== host.state.connectionStatus) {
    host.refreshOwnershipOutlines();
  }

  // Kick off the region-view fade/lift animation whenever the
  // player toggles between surface and subsurface views.
  if (prevState.regionView !== host.state.regionView) {
    if (host.state.regionView === 'subsurface' && !host.state.tutorialSubsurfaceVisited) {
      host.setState({ tutorialSubsurfaceVisited: true });
    }
    host._beginViewTransition(host.state.regionView);
    // Crossfade the audio layer in parallel. Subsurface view
    // suppresses the user's ambience + music and plays the cave +
    // underwater cue for as long as the player is below the
    // surface. Surface restores the user's chosen tracks.
    if (host.state.regionView === 'subsurface') {
      // Seed the cave/underwater mix from the current body counts
      // BEFORE starting the layer fades, so the first entry ramps
      // straight to the correct ratio rather than fading in at the
      // default and then re-fading to the live mix.
      const counts = host._countSubsurfaceCells();
      setSubsurfaceMix(counts.water, counts.underground);
      enterSubsurfaceAmbience();
    } else {
      exitSubsurfaceAmbience();
    }
  }

  // Pick up Space-was-armed when hand hover transitions to a card.
  // Mesh-hover changes go through handleMouseMove → tryOpen directly;
  // this branch catches the DOM hand-fan path.
  if (
    prevState.hoveredHandIndex !== host.state.hoveredHandIndex &&
    host.state.hoveredHandIndex >= 0 &&
    !host.state.inspectedCard
  ) {
    host.tryOpenInspectorFromArmed();
  }

  // Feed hand hovers into the streamer-facing card preview drawer
  // (sticky) AND the alt-zoom bus (live). Active hand resolution
  // mirrors tryOpenInspectorFromArmed (see line ~1838) so the
  // preview matches whichever hand the user is hovering in casual
  // sandbox. We fire on every transition. Including hand→empty.
  // so the alt-zoom overlay can hide as soon as the cursor leaves
  // the hand fan.
  if (prevState.hoveredHandIndex !== host.state.hoveredHandIndex) {
    const handCard = host.state.hoveredHandIndex >= 0
      ? host.getActiveHandCards()[host.state.hoveredHandIndex]
      : null;
    if (handCard) host._setFocusedPreviewCard(handCard);
    setZoomBusHoveredCard(handCard);
  }

  // When the deck picker closes (via any path. Explicit close,
  // confirm flow, reset), re-fire the tutorial check.
  if (prevState.showDeckPicker && !host.state.showDeckPicker) {
    host._scheduleBoardTutorialCheck(400);
  }
}

export function handleResize(host) {
  host.scene?.resize();
}
